import React from 'react'
import { Bot, Library } from 'lucide-react'
import OverviewPanel, { BandHeading } from './OverviewPanel'
import CronStatusChip from './CronStatusChip'
import { cronMetaForJob } from '../../lib/cronJobMeta'
import { formatRelativeTime } from './overviewFormat'

/**
 * AI Ops Logs — digest cron + Basecamp sync, directly under AI Spotlight.
 */
export default function AiOpsLogs({
  stackedUnderSpotlight = false,
  showDigest,
  showSync,
  isSuperAdmin,
  loading,
  triggeringDigest,
  onRunDigest,
  digestCronJob,
  cronGroups,
  syncCronJobs,
  syncLogs,
}) {
  if (!showDigest && !showSync) return null

  const zoneClass = [
    'overview-band',
    'overview-ai-zone',
    stackedUnderSpotlight ? 'overview-ai-zone--ops' : '',
    'overview-fade-in',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={zoneClass} aria-label="AI Ops Logs">
      <BandHeading
        title="AI Ops Logs"
        subtitle="Digest pipeline and Basecamp sync health"
        badge="Live"
      />
      <div className="overview-two-col">
        {showDigest ? (
          <OverviewPanel
            variant="ai"
            title="AI digest cron"
            description="Daily analyzer → task creator pipeline"
            icon={Bot}
            footerHref="/admin/ai/agents-logs?tab=cron"
            footerLabel="View AI cron logs"
            headerActions={
              isSuperAdmin ? (
                <button
                  type="button"
                  className="overview-btn-primary"
                  disabled={loading || triggeringDigest}
                  onClick={onRunDigest}
                  title="Production digest pipeline (same as 07:00 UTC cron)"
                >
                  {triggeringDigest
                    ? 'Running briefing…'
                    : 'Run Daily AI Briefing (Basecamp Digest Cron)'}
                </button>
              ) : null
            }
          >
            {digestCronJob ? (
              <div className="overview-cron-meta">
                <CronStatusChip status={digestCronJob.last_run_status} />
                <span>
                  {cronMetaForJob(digestCronJob.jobname).scheduleHuman}
                  {' · '}
                  Last run {formatRelativeTime(digestCronJob.last_run_start)}
                </span>
              </div>
            ) : null}
            <div className="overview-mini-table-wrap">
              <table className="overview-mini-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Trigger</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && !cronGroups.length ? (
                    <tr>
                      <td colSpan={3} className="overview-muted-copy">
                        Loading…
                      </td>
                    </tr>
                  ) : cronGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="overview-muted-copy">
                        No digest runs yet
                      </td>
                    </tr>
                  ) : (
                    cronGroups.map((g) => (
                      <tr key={g.run_group_id}>
                        <td>{g.started_at ? new Date(g.started_at).toLocaleString() : '—'}</td>
                        <td>{g.trigger || '—'}</td>
                        <td>
                          <CronStatusChip status={g.aggregateStatus} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </OverviewPanel>
        ) : null}

        {showSync ? (
          <OverviewPanel
            variant="ai"
            title="Basecamp sync"
            description="Scheduled sync jobs and recent runs"
            icon={Library}
            footerHref="/admin/basecamp/projects?tab=logs"
            footerLabel="View sync logs"
          >
            {syncCronJobs.slice(0, 2).map((job) => (
              <div key={job.jobname} className="overview-cron-row">
                <strong>{cronMetaForJob(job.jobname).title}</strong>
                <CronStatusChip status={job.last_run_status} />
                <span className="overview-muted-copy">{formatRelativeTime(job.last_run_start)}</span>
              </div>
            ))}
            <div className="overview-mini-table-wrap">
              <table className="overview-mini-table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th>Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && !syncLogs.length ? (
                    <tr>
                      <td colSpan={3}>Loading…</td>
                    </tr>
                  ) : syncLogs.length === 0 ? (
                    <tr>
                      <td colSpan={3}>No sync runs yet</td>
                    </tr>
                  ) : (
                    syncLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.started_at ? new Date(log.started_at).toLocaleString() : '—'}</td>
                        <td>
                          <CronStatusChip status={log.status} />
                        </td>
                        <td>{log.trigger || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </OverviewPanel>
        ) : null}
      </div>
    </section>
  )
}
